import { createSelector } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';

import { InsightsState } from 'app-redux/InsightsState';
import { DiseaseActivitiesSortOptions } from 'models/SortByOptions';
import { containsNoCaseNoLocale } from 'utils/stringHelpers';

import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';

const getDiseaseActivitiesSortBy = (state: InsightsState) => state.diseaseActivitiesInLocation.sortBy;
const isLoading = (state: InsightsState) => state.diseaseActivitiesInLocation.isLoading;
const hasError = (state: InsightsState) => state.diseaseActivitiesInLocation.hasError;
const getSearchText = (state: InsightsState) => state.diseaseActivitiesInLocation.searchString;
const getAllDiseaseIds = (state: InsightsState) => state.diseaseActivitiesInLocation.allDiseaseIds;

const getFilteredDiseaseIds = createSelector(
  getAllDiseaseIds,
  state => state.diseaseActivitiesInLocation.diseaseInformations,
  state => state.diseaseActivitiesInLocation.nestedCaseCounts,
  state => state.diseaseActivitiesInLocation.importationRisks,
  state => state.diseaseActivitiesInLocation.lastUpdatedEventDates,
  state => state.diseaseActivitiesInLocation.searchString,
  state => state.diseaseActivitiesInLocation.sortBy,
  (allDiseaseIds, diseaseInformationByDiseaseId, nestedCaseCounts, importationRiskByDiseaseId, lastUpdatedEventDates, searchString, sortBy) => {
    const sortableDiseases = allDiseaseIds.map(id => ({
      id: id,
      name: diseaseInformationByDiseaseId[id].name,
      cases: nestedCaseCounts[id]?.locationCaseCounts?.caseCounts?.reportedCases ?? -1,
      lastUpdatedEventDate: lastUpdatedEventDates[id],
      risk: importationRiskByDiseaseId[id],
    }));
    const sortOption = DiseaseActivitiesSortOptions.find(so => so.value === sortBy);
    return orderBy(sortableDiseases, sortOption.keys, sortOption.orders)
      .filter(x => containsNoCaseNoLocale(x.name, searchString))
      .map(d => d.id);
  }
);
const isSelectedDiseaseInvalid = createSelector(diseaseActivityPanelsSelectors.disacParams, getAllDiseaseIds, (disacParams, allDiseaseIds) =>
  disacParams.diseaseId ? !allDiseaseIds.includes(disacParams.diseaseId) : false
);
const getDiseaseName = (diseaseId: number) => (state: InsightsState) => state.diseaseActivitiesInLocation.diseaseInformations[diseaseId]?.name;
const getOutbreakPotentialCategory = (diseaseId: number) => (state: InsightsState) =>
  state.diseaseActivitiesInLocation.outbreakPotentialCategories[diseaseId];
const getCaseCounts = (diseaseId: number) => (state: InsightsState) => state.diseaseActivitiesInLocation.caseCounts[diseaseId];
const getGlobalCaseCounts = (diseaseId: number) => (state: InsightsState) => getCaseCounts(diseaseId)(state)?.reportedCases ?? null;
const getRiskModel = (diseaseId: number) => (state: InsightsState) => state.diseaseActivitiesInLocation.importationRisks[diseaseId];
const getNestedCaseCounts = (diseaseId: number) => (state: InsightsState) => state.diseaseActivitiesInLocation.nestedCaseCounts[diseaseId];
const getProximalReportedCases = (diseaseId: number) => (state: InsightsState) =>
  getNestedCaseCounts(diseaseId)(state)?.locationCaseCounts?.caseCounts?.reportedCases ?? null;
const getProximalCountryReportedCases = (diseaseId: number) => (state: InsightsState) =>
  getNestedCaseCounts(diseaseId)(state)?.countryCaseCounts?.caseCounts?.reportedCases;

// diseaseActivitiesInLocationSelectors
export default {
  getAllDiseaseIds,
  getFilteredDiseaseIds,
  isSelectedDiseaseInvalid,
  getDiseaseName,
  isLoading,
  hasError,
  getSearchText,
  getOutbreakPotentialCategory,
  getCaseCounts,
  getGlobalCaseCounts,
  getRiskModel,
  getDiseaseActivitiesSortBy,
  getNestedCaseCounts,
  getProximalReportedCases,
  getProximalCountryReportedCases,
};
